import React, { useEffect, useState } from 'react';
import { Trash2, Edit, UserCheck, Shield, Download, ChevronLeft, ChevronRight, X, Plus, Search } from 'lucide-react';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State untuk Fitur Filter / Pencarian
  const [searchQuery, setSearchQuery] = useState('');

  // State untuk Pagination & Items Per Page
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Default 10 data per halaman

  // State untuk Modal Edit (ditambahkan password)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', full_name: '', username: '', role: '', kecamatan: '', password: '' });
  const [isSaving, setIsSaving] = useState(false);

  // State untuk Modal Tambah User
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({ full_name: '', username: '', role: 'guru', password: '', kecamatan: '' });
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Kembali ke halaman 1 jika user sedang mengetik pencarian
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const res = await fetch(`${BASE_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.status === 403) {
        alert("Anda bukan Admin!");
        return;
      }

      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // --- LOGIKA FILTER SEMUA KOLOM ---
  const filteredUsers = users.filter((u) => {
    const query = searchQuery.toLowerCase();
    return (
      String(u.id).toLowerCase().includes(query) ||
      (u.full_name || '').toLowerCase().includes(query) ||
      (u.username || '').toLowerCase().includes(query) ||
      (u.role || '').toLowerCase().includes(query) ||
      (u.kecamatan || '').toLowerCase().includes(query)
    );
  });

  // --- LOGIKA PAGINATION (Berdasarkan Data yang Difilter) ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  // Fungsi untuk mengubah jumlah data per halaman
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset ke halaman 1 agar tidak error out-of-bounds
  };

  // --- FUNGSI EXPORT CSV ---
  const handleExportCSV = () => {
    const headers = ['ID', 'Nama Lengkap', 'Username', 'Role', 'Kecamatan'];
    const csvData = filteredUsers.map(u => 
      `${u.id},${u.full_name},${u.username},${u.role},${u.kecamatan || '-'}`
    );
    const csvContent = [headers.join(','), ...csvData].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'data_pengguna.csv';
    link.click();
  };

  // --- FUNGSI DELETE ALL ---
  const handleDeleteAll = async () => {
    if (window.confirm('APAKAH ANDA YAKIN INGIN MENGHAPUS SEMUA DATA PENGGUNA? Tindakan ini tidak bisa dibatalkan.')) {
      try {
        setUsers([]);
        alert('Semua data berhasil dihapus dari tampilan!');
      } catch (err) {
        console.error("Gagal menghapus semua data:", err);
      }
    }
  };

  // --- FUNGSI DELETE INDIVIDUAL ---
  const handleDelete = async (id) => {
    if (window.confirm(`Yakin ingin menghapus user dengan ID #${id}?`)) {
      try {
        setUsers(users.filter(u => u.id !== id));
      } catch (err) {
        console.error("Gagal menghapus data:", err);
      }
    }
  };

  // --- FUNGSI BUKA MODAL EDIT ---
  const openEditModal = (user) => {
    setEditForm({
      id: user.id,
      full_name: user.full_name,
      username: user.username,
      role: user.role,
      kecamatan: user.kecamatan || '',
      password: '' // Reset field password saat modal dibuka
    });
    setIsModalOpen(true);
  };

  // --- FUNGSI SIMPAN EDIT ---
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const payload = {
        full_name: editForm.full_name,
        username: editForm.username,
        role: editForm.role,
        kecamatan: editForm.kecamatan
      };

      // Hanya kirim password jika field diisi
      if (editForm.password && editForm.password.trim() !== '') {
        payload.password = editForm.password;
      }

      const res = await fetch(`${BASE_URL}/api/users/${editForm.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setUsers(users.map(u => u.id === editForm.id ? { ...u, ...editForm } : u));
        setIsModalOpen(false); 
      } else {
        const errorData = await res.json();
        alert(`Gagal: ${errorData.msg}`);
      }
    } catch (err) {
      console.error("Gagal mengupdate data:", err);
      alert("Terjadi kesalahan jaringan.");
    } finally {
      setIsSaving(false);
    }
  };

  // --- FUNGSI TAMBAH USER ---
  const handleAddUser = async (e) => {
    e.preventDefault();
    setIsAdding(true);

    try {
      const token = localStorage.getItem('token');
      const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const res = await fetch(`${BASE_URL}/api/users`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(addForm)
      });
      
      const data = await res.json();

      if (res.ok) {
        setUsers([...users, data]);
        setIsAddModalOpen(false);
        setAddForm({ full_name: '', username: '', role: 'guru', password: '', kecamatan: '' });
        alert("Berhasil menambah pengguna baru!");
      } else {
        alert(`Gagal: ${data.msg}`);
      }
    } catch (err) {
      console.error("Gagal menambah data:", err);
      alert("Terjadi kesalahan jaringan.");
    } finally {
      setIsAdding(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Memuat data pengguna...</div>;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-slate-900">Manajemen Pengguna</h1>
          <p className="text-slate-500 text-sm mt-1">Daftar semua akun yang terdaftar dalam sistem.</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <div className="bg-slate-100 px-4 py-2 rounded-lg text-xs font-bold text-slate-600 mr-2">
            Total: {filteredUsers.length} Akun
          </div>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
          >
            <Plus size={16} /> Tambah User
          </button>
          <button 
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-3 py-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors text-sm font-medium"
          >
            <Download size={16} /> Export CSV
          </button>
          <button 
            onClick={handleDeleteAll}
            className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
          >
            <Trash2 size={16} /> Delete All
          </button>
        </div>
      </div>

      {/* SEARCH BAR / FILTER INPUT */}
      <div className="mb-4 flex items-center bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
        <Search size={18} className="text-slate-400 mr-3" />
        <input
          type="text"
          placeholder="Cari berdasarkan ID, Nama, NIP, Kecamatan, atau Role..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full focus:outline-none text-sm text-slate-700 bg-transparent placeholder-slate-400"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="text-slate-400 hover:text-slate-600">
            <X size={16} />
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 font-bold text-slate-700 uppercase tracking-wider text-xs">ID</th>
                <th className="px-6 py-4 font-bold text-slate-700 uppercase tracking-wider text-xs">Nama Lengkap</th>
                <th className="px-6 py-4 font-bold text-slate-700 uppercase tracking-wider text-xs">Username / NIP</th>
                <th className="px-6 py-4 font-bold text-slate-700 uppercase tracking-wider text-xs">Kecamatan</th>
                <th className="px-6 py-4 font-bold text-slate-700 uppercase tracking-wider text-xs">Role</th>
                <th className="px-6 py-4 font-bold text-slate-700 uppercase tracking-wider text-xs text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentUsers.length > 0 ? (
                currentUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-slate-500">#{u.id}</td>
                    <td className="px-6 py-4 font-medium text-slate-900">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {u.full_name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        {u.full_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600 font-mono text-xs">{u.username}</td>
                    <td className="px-6 py-4 text-slate-600">{u.kecamatan || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                        u.role === 'admin' 
                          ? 'bg-purple-100 text-purple-700 border border-purple-200' 
                          : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                      }`}>
                        {u.role === 'admin' ? <Shield size={12}/> : <UserCheck size={12}/>}
                        {u.role?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={() => openEditModal(u)}
                        className="text-slate-400 hover:text-blue-600 mr-3 transition-colors"
                        title="Edit User"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        onClick={() => handleDelete(u.id)}
                        className="text-slate-400 hover:text-red-600 transition-colors"
                        title="Delete User"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                    Tidak ada data pengguna yang cocok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION BAR */}
        {filteredUsers.length > 0 && (
          <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="text-sm text-slate-500">
              Menampilkan <span className="font-medium text-slate-700">{indexOfFirstItem + 1}</span> hingga <span className="font-medium text-slate-700">{Math.min(indexOfLastItem, filteredUsers.length)}</span> dari <span className="font-medium text-slate-700">{filteredUsers.length}</span> data
            </span>
            
            <div className="flex items-center gap-4">
              
              {/* DROPDOWN ITEMS PER PAGE */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Tampilkan:</span>
                <select 
                  value={itemsPerPage} 
                  onChange={handleItemsPerPageChange}
                  className="bg-white border border-slate-300 text-slate-700 text-sm rounded focus:ring-blue-500 focus:border-blue-500 block px-2 py-1 outline-none"
                >
                  <option value={10}>10</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
              </div>

              {/* NAVIGASI HALAMAN */}
              <div className="flex gap-2">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded bg-white border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded bg-white border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MODAL EDIT */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-semibold text-slate-800">Edit Pengguna #{editForm.id}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSaveEdit} className="p-6">
             <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nama Lengkap</label>
                  <input 
                    type="text" 
                    required
                    value={editForm.full_name}
                    onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Username / NIP</label>
                  <input 
                    type="text" 
                    required
                    value={editForm.username}
                    onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Kecamatan</label>
                  <input 
                    type="text" 
                    value={editForm.kecamatan}
                    onChange={(e) => setEditForm({...editForm, kecamatan: e.target.value})}
                    placeholder="Contoh: Duren Sawit"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* --- INPUT PASSWORD BARU (OPSIONAL) --- */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password Baru (Opsional)</label>
                  <input 
                    type="text" 
                    value={editForm.password}
                    onChange={(e) => setEditForm({...editForm, password: e.target.value})}
                    placeholder="Kosongkan jika tidak ganti password"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select 
                    value={editForm.role}
                    onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="guru">Guru</option>
                    <option value="satlak">Satlak</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isSaving ? 'Menyimpan...' : 'Simpan Perubahan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL TAMBAH USER BARU */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100 bg-slate-50/50">
              <h3 className="font-semibold text-slate-800">Tambah Pengguna Baru</h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddUser} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nama Lengkap</label>
                  <input 
                    type="text" 
                    required
                    value={addForm.full_name}
                    onChange={(e) => setAddForm({...addForm, full_name: e.target.value})}
                    placeholder="Contoh: Budi Santoso"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Username / NIP</label>
                  <input 
                    type="text" 
                    required
                    value={addForm.username}
                    onChange={(e) => setAddForm({...addForm, username: e.target.value})}
                    placeholder="Masukkan NIP atau Username"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password (Opsional)</label>
                  <input 
                    type="text" 
                    value={addForm.password}
                    onChange={(e) => setAddForm({...addForm, password: e.target.value})}
                    placeholder="Kosongkan agar password = username"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Kecamatan</label>
                  <input 
                    type="text" 
                    value={addForm.kecamatan}
                    onChange={(e) => setAddForm({...addForm, kecamatan: e.target.value})}
                    placeholder="Contoh: Duren Sawit"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                  <select 
                    value={addForm.role}
                    onChange={(e) => setAddForm({...addForm, role: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="guru">Guru</option>
                    <option value="satlak">Satlak</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  disabled={isAdding}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isAdding ? 'Menyimpan...' : 'Simpan User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;